/** localStorage 용량 초과 시에만 인증·필수 설정은 남기고 캐시를 비운다. */

import { isSubscriptionPassStorageKey, notifySubscriptionPassChanged } from './subscriptionPassStorage';

const PRESERVE_EXACT = new Set([
  'curriculumplanner-login-email',
  'curriculumplanner-school-id',
  'curriculumplanner-grade-id',
  'curriculum-theme',
]);

/**
 * 브라우저별 origin localStorage 한도(대략):
 * - Chrome / Edge / Firefox: 보통 **약 5~10MB**
 * - Safari: 보통 **약 5MB**
 * 정확한 값은 브라우저·프라이빗 모드에 따라 다르며, API로 조회할 수 없다.
 */
export const LOCAL_STORAGE_QUOTA_HINT_MB = '약 5~10MB(브라우저·origin별)';

/** 한 번에 비울 목표(바이트). 인증 토큰은 보통 수~수십 KB라 여유만 확보 */
const DEFAULT_FREE_TARGET_BYTES = 512 * 1024;

function isPreservedKey(key: string): boolean {
  if (PRESERVE_EXACT.has(key)) return true;
  if (isSubscriptionPassStorageKey(key)) return true;
  // Supabase GoTrue 세션
  if (key.startsWith('sb-')) return true;
  // 데일리 노트 보기 잠금 — quota 정리 시에도 유지
  if (key.startsWith('cp.dailyNote.viewLock.')) return true;
  return false;
}

function isDisposableCacheKey(key: string): boolean {
  if (isPreservedKey(key)) return false;
  /** UI 선호 설정 — quota 정리 시에도 유지 */
  if (key.startsWith('cp-member-profile-display-mode:')) return false;
  if (key.startsWith('cp-class-point-gauge-mode:')) return false;
  if (key.startsWith('cp.') || key.startsWith('cp-')) return true;
  if (key.startsWith('curriculum-landing')) return true;
  return false;
}

export function isLocalStorageQuotaError(err: unknown): boolean {
  if (err instanceof DOMException) {
    if (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
    if (err.code === 22 || err.code === 1014) return true;
  }
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /exceeded the quota|quota.?exceeded|setItem.*Storage/i.test(msg);
}

/** UTF-16 대략 바이트(키+값). */
export function estimateLocalStorageBytes(): number {
  if (typeof window === 'undefined') return 0;
  let chars = 0;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      chars += key.length;
      const val = window.localStorage.getItem(key);
      if (val) chars += val.length;
    }
  } catch {
    return 0;
  }
  return chars * 2;
}

/** JSON 캐시의 savedAt(ms) — 없으면 0(가장 오래됨으로 취급해 우선 삭제 후보) */
function readSavedAtMs(raw: string): number {
  if (!raw || raw[0] !== '{') return 0;
  try {
    const parsed = JSON.parse(raw) as { savedAt?: unknown };
    if (typeof parsed.savedAt === 'number' && Number.isFinite(parsed.savedAt) && parsed.savedAt > 0) {
      return parsed.savedAt;
    }
    if (typeof parsed.savedAt === 'string') {
      const t = Date.parse(parsed.savedAt);
      return Number.isFinite(t) ? t : 0;
    }
  } catch {
    /* ignore */
  }
  return 0;
}

type DisposableEntry = { key: string; size: number; savedAt: number };

function listDisposableEntries(): DisposableEntry[] {
  const ranked: DisposableEntry[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !isDisposableCacheKey(key)) continue;
      const val = window.localStorage.getItem(key) ?? '';
      ranked.push({
        key,
        size: (key.length + val.length) * 2,
        savedAt: readSavedAtMs(val),
      });
    }
  } catch {
    return [];
  }
  // 1) 오래된 savedAt 우선 2) 같으면 큰 용량 우선
  ranked.sort((a, b) => {
    const aAt = a.savedAt > 0 ? a.savedAt : 0;
    const bAt = b.savedAt > 0 ? b.savedAt : 0;
    if (aAt !== bAt) {
      // savedAt 없음(0)은 오래된 것으로 보고 먼저
      if (aAt === 0 && bAt === 0) return b.size - a.size;
      if (aAt === 0) return -1;
      if (bAt === 0) return 1;
      return aAt - bAt;
    }
    return b.size - a.size;
  });
  return ranked;
}

/**
 * quota 복구용: 오래된 캐시(savedAt) → 큰 용량 순으로 `targetBytes`만큼 삭제.
 * 로그인·모달 오픈 시 무조건 호출하지 말 것 — setItem 실패 시에만.
 * @returns 삭제된 키 개수
 */
export function freeDisposableLocalStorageCaches(
  targetBytes: number = DEFAULT_FREE_TARGET_BYTES,
  options?: { preserveKeys?: string[] }
): number {
  if (typeof window === 'undefined') return 0;

  const preserve = new Set(options?.preserveKeys ?? []);
  const ranked = listDisposableEntries().filter((e) => !preserve.has(e.key));
  let freed = 0;
  let removed = 0;

  for (const { key, size } of ranked) {
    if (freed >= targetBytes) break;
    try {
      window.localStorage.removeItem(key);
      if (isSubscriptionPassStorageKey(key)) {
        notifySubscriptionPassChanged();
      }
      freed += size;
      removed += 1;
    } catch {
      /* ignore */
    }
  }

  return removed;
}

/**
 * 여전히 공간이 부족하면 남은 disposable 캐시를 모두 비운다 (최후 수단).
 */
export function freeAllDisposableLocalStorageCaches(options?: {
  preserveKeys?: string[];
}): number {
  if (typeof window === 'undefined') return 0;
  const preserve = new Set(options?.preserveKeys ?? []);
  const ranked = listDisposableEntries().filter((e) => !preserve.has(e.key));
  let removed = 0;
  for (const { key } of ranked) {
    try {
      window.localStorage.removeItem(key);
      removed += 1;
    } catch {
      /* ignore */
    }
  }
  return removed;
}

function tryLocalStorageSetItem(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (err) {
    if (!isLocalStorageQuotaError(err)) throw err;
    return false;
  }
}

/**
 * 캐시 덮어쓰기.
 * - 같은 키의 **옛 값을 먼저 지운 뒤** 새 값을 씀 (용량 확보 + 실패 시 stale 잔존 방지).
 * - quota 시 다른 disposable 캐시를 비우고 재시도.
 * - 최종 실패하면 해당 키는 비어 있음 → 다음 읽기는 miss → DB.
 * @returns 저장 성공 여부
 */
export function localStorageSetItemWithQuotaRecovery(key: string, value: string): boolean {
  if (typeof window === 'undefined') return false;
  const preserve = { preserveKeys: [key] };

  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }

  if (tryLocalStorageSetItem(key, value)) return true;
  freeDisposableLocalStorageCaches(DEFAULT_FREE_TARGET_BYTES, preserve);
  if (tryLocalStorageSetItem(key, value)) return true;
  freeAllDisposableLocalStorageCaches(preserve);
  if (tryLocalStorageSetItem(key, value)) return true;
  return false;
}
